import React, { useState, useEffect } from 'react';
import { ServerStatus } from './ServerStatus';
import { EventsHeader } from './EventsHeader';
import { api } from '../services/api';

export const Dashboard = ({ events }) => {
  const [loginStats, setLoginStats] = useState({
    totalLogins: 0,
    todayLogins: 0,
    uniqueUsers: 0
  });

  useEffect(() => {
    // Fetch login statistics when component mounts
    fetchLoginStats();
  }, []);

  const fetchLoginStats = async () => {
    try {
      const data = await api.fetchLoginStats();
      setLoginStats(data);
    } catch (error) {
      console.error('Error fetching login statistics:', error);
    }
  };

  const getEventStats = () => {
    const now = new Date();
    
    // Sets to track unique values
    const uniqueClients = new Set();
    const uniqueLocations = new Set(); // Based on cities
    const uniqueSites = new Set();
    const uniquePanels = new Set();
    
    const stats = {
      total: events.length,
      acknowledged: 0,
      realAlarms: 0,        // CONFIRMED status
      falseAlarms: 0,       // FALSE status
      pendingAlarms: 0,     // PENDING status
      defaultStatus: 0,     // DEFAULT status (non-HIGH severity)
      averageAckTime: 0,
      totalAckTime: 0,
      recentEvents: 0,
      eventsByDescription: {},
      severityCounts: {
        HIGH: 0,
        MEDIUM: 0,
        WARNING: 0,
        INFO: 0,
        UNKNOWN: 0
      },
      categoryCounts: {
        ALARM: 0,
        SYSTEM: 0,
        MAINTENANCE: 0,
        POWER: 0,
        COMMUNICATION: 0,
        DEVICE: 0,
        VIDEO_ALARM: 0,
        ACCESS: 0,
        UNKNOWN: 0
      },
      // Location stats for detailed view
      locationStats: {
        clients: {},
        provinces: {},
        cities: {},
        sites: {},
        panels: {}
      },
      // Unique count stats for summary
      uniqueCounts: {
        clients: 0,
        locations: 0,
        sites: 0,
        panels: 0
      }
    };
  
    events.forEach(event => {
      if (event.acknowledged) {
        stats.acknowledged++;
        if (event.acknowledgedAt && event.timestamp) {
          const ackTime = new Date(event.acknowledgedAt) - new Date(event.timestamp);
          stats.totalAckTime += ackTime;
        }
      }
  
      // Count events based on alarmStatus
      if (event.alarmStatus === 'CONFIRMED') {
        stats.realAlarms++;
      } else if (event.alarmStatus === 'FALSE') {
        stats.falseAlarms++;
      } else if (event.alarmStatus === 'PENDING') {
        stats.pendingAlarms++;
      } else if (event.alarmStatus === 'DEFAULT') {
        stats.defaultStatus++;
      }
  
      // Rest of the function remains the same
      if (event.description) {
        stats.eventsByDescription[event.description] = 
          (stats.eventsByDescription[event.description] || 0) + 1;
      }
  
      if (event.severity) {
        stats.severityCounts[event.severity] = 
          (stats.severityCounts[event.severity] || 0) + 1;
      }
  
      if (event.category) {
        stats.categoryCounts[event.category] = 
          (stats.categoryCounts[event.category] || 0) + 1;
      } else {
        stats.categoryCounts["UNKNOWN"]++;
      }
  
      if ((now - new Date(event.timestamp)) < 24 * 60 * 60 * 1000) {
        stats.recentEvents++;
      }
      
      // Process location data
      if (event.location) {
        // Client tracking
        if (event.location.clientName) {
          const clientKey = `${event.location.clientName} (${event.location.clientCode})`;
          stats.locationStats.clients[clientKey] = (stats.locationStats.clients[clientKey] || 0) + 1;
          uniqueClients.add(clientKey);
        }
  
        // Province stats
        if (event.location.province) {
          stats.locationStats.provinces[event.location.province] = 
            (stats.locationStats.provinces[event.location.province] || 0) + 1;
        }
  
        // City stats (used for location count)
        if (event.location.city) {
          stats.locationStats.cities[event.location.city] = 
            (stats.locationStats.cities[event.location.city] || 0) + 1;
          uniqueLocations.add(event.location.city);
        }
  
        // Site stats
        if (event.location.siteCode) {
          const siteKey = `Site ${event.location.siteCode}`;
          stats.locationStats.sites[siteKey] = (stats.locationStats.sites[siteKey] || 0) + 1;
          uniqueSites.add(siteKey);
        }
  
        // Panel stats
        if (event.location.panelCode) {
          const panelKey = `Panel ${event.location.panelCode}`;
          stats.locationStats.panels[panelKey] = (stats.locationStats.panels[panelKey] || 0) + 1;
          uniquePanels.add(panelKey);
        }
      }
    });
  
    if (stats.acknowledged > 0) {
      stats.averageAckTime = stats.totalAckTime / stats.acknowledged;
    }
    
    // Set the unique counts
    stats.uniqueCounts = {
      clients: uniqueClients.size,
      locations: uniqueLocations.size,
      sites: uniqueSites.size,
      panels: uniquePanels.size
    };
  
    return stats;
  };

  const stats = getEventStats();

  // Get the top 5 alarm types by description
  const getTopAlarmTypes = () => {
    return Object.entries(stats.eventsByDescription)
      .sort(([, countA], [, countB]) => countB - countA)
      .slice(0, 5);
  };

  const topAlarmTypes = getTopAlarmTypes();

  // Get colors for severity levels
  const getSeverityColor = (severity) => {
    const colors = {
      HIGH: { bg: 'bg-red-50', text: 'text-red-800' },
      MEDIUM: { bg: 'bg-orange-50', text: 'text-orange-800' },
      WARNING: { bg: 'bg-yellow-50', text: 'text-yellow-800' },
      INFO: { bg: 'bg-blue-50', text: 'text-blue-800' },
      UNKNOWN: { bg: 'bg-gray-50', text: 'text-gray-800' }
    };
    return colors[severity] || colors.UNKNOWN;
  };

  // Get colors for category types
  const getCategoryColor = (category) => {
    const colors = {
      ALARM: { bg: 'bg-red-50', text: 'text-red-800' },
      SYSTEM: { bg: 'bg-purple-50', text: 'text-purple-800' },
      MAINTENANCE: { bg: 'bg-blue-50', text: 'text-blue-800' },
      POWER: { bg: 'bg-yellow-50', text: 'text-yellow-800' },
      COMMUNICATION: { bg: 'bg-indigo-50', text: 'text-indigo-800' },
      DEVICE: { bg: 'bg-green-50', text: 'text-green-800' },
      VIDEO_ALARM: { bg: 'bg-pink-50', text: 'text-pink-800' },
      ACCESS: { bg: 'bg-cyan-50', text: 'text-cyan-800' },
      UNKNOWN: { bg: 'bg-gray-50', text: 'text-gray-800' }
    };
    return colors[category] || colors.UNKNOWN;
  };
  
  // Helper function to get background color based on index (for variety)
  const getBgColor = (index) => {
    const colors = [
      'bg-blue-50', 'bg-green-50', 'bg-purple-50', 
      'bg-yellow-50', 'bg-pink-50', 'bg-indigo-50',
      'bg-red-50', 'bg-orange-50', 'bg-teal-50'
    ];
    return colors[index % colors.length];
  };
  
  // Helper function to get text color based on index
  const getTextColor = (index) => {
    const colors = [
      'text-blue-800', 'text-green-800', 'text-purple-800', 
      'text-yellow-800', 'text-pink-800', 'text-indigo-800',
      'text-red-800', 'text-orange-800', 'text-teal-800'
    ];
    return colors[index % colors.length];
  };

  // Calculate the percentage for alarm types
  const calculatePercentage = (count) => {
    return ((count / stats.total) * 100).toFixed(1);
  };

  return (
    <div className="py-20 px-6">
      {/* Server Status */}
      <div className="mb-6">
        <ServerStatus />
      </div>

      {/* Event Header moved from Events component */}
      <div className="mb-6">
        <EventsHeader events={events} />
      </div>

      {/* Top 5 Alarm Types */}
      <div className="bg-white rounded-lg shadow-sm p-6 mb-6">
        <h3 className="text-xl font-semibold mb-4">Top 5 Alarm Types</h3>
        <div className="space-y-3">
          {topAlarmTypes.map(([alarmType, count], index) => (
            <div key={alarmType} className="flex items-center">
              <div className={`w-full h-10 rounded-lg ${getBgColor(index)} relative overflow-hidden`}>
                <div 
                  className={`absolute h-full ${getBgColor(index)} border-r border-white`} 
                  style={{ width: `${(count / stats.total) * 100}%` }}
                ></div>
                <div className="absolute inset-0 flex items-center justify-between px-3">
                  <span className={`font-medium ${getTextColor(index)}`}>{alarmType}</span>
                  <span className={`font-medium ${getTextColor(index)}`}>
                    {count} ({calculatePercentage(count)}%)
                  </span>
                </div>
              </div>
            </div>
          ))}
          
          {topAlarmTypes.length === 0 && (
            <div className="text-gray-500 text-center py-4">No alarm data available</div>
          )}
        </div>
      </div>

      {/* Severity and Category Section */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
        {/* Severity Distribution */}
        <div className="bg-white rounded-lg shadow-sm p-6">
          <h3 className="text-xl font-semibold mb-4">
            Alarm Tags
          </h3>
          <div className="space-y-3">
            {Object.entries(stats.severityCounts)
              .filter(([severity, count]) => count > 0)
              .sort(([severityA, a], [severityB, b]) => b - a)
              .map(([severity, count]) => {
                const colors = getSeverityColor(severity);
                return (
                  <div key={severity} className="flex items-center">
                    <div className={`w-full h-8 rounded-lg ${colors.bg} relative overflow-hidden`}>
                      <div 
                        className={`absolute h-full ${colors.bg} border-r border-white`} 
                        style={{ width: `${(count / stats.total) * 100}%` }}
                      ></div>
                      <div className="absolute inset-0 flex items-center justify-between px-3">
                        <span className={`font-medium ${colors.text}`}>{severity}</span>
                        <span className={`font-medium ${colors.text}`}>{count} ({((count / stats.total) * 100).toFixed(1)}%)</span>
                      </div>
                    </div>
                  </div>
                );
              })}
          </div>
        </div>

        {/* Category Distribution */}
        <div className="bg-white rounded-lg shadow-sm p-6">
          <h3 className="text-xl font-semibold mb-4">
            Event Categories
          </h3>
          <div className="grid grid-cols-2 gap-3">
            {Object.entries(stats.categoryCounts)
              .filter(([category, count]) => count > 0)
              .sort(([categoryA, a], [categoryB, b]) => b - a)
              .map(([category, count]) => {
                const colors = getCategoryColor(category);
                return (
                  <div key={category} className={`${colors.bg} rounded-lg p-3 text-center`}>
                    <div className={`text-xl font-bold ${colors.text}`}>
                      {count}
                    </div>
                    <div className={`text-sm ${colors.text}`}>{category}</div>
                  </div>
                );
              })}
          </div>
        </div>
      </div>

      {/* Location Summary Card */}
      <div className="bg-white rounded-lg shadow-sm p-6 mb-6">
        <h3 className="text-xl font-semibold mb-4">Sites and Devices</h3>
        <div className="flex flex-wrap justify-between items-center gap-4">
          {/* Clients */}
          <div className="flex flex-col items-center p-3 bg-blue-100 text-blue-800 rounded-lg">
            <div className="text-2xl font-bold">{stats.uniqueCounts.clients}</div>
            <div className="text-sm">Clients</div>
          </div>
          
          {/* Locations (Cities) */}
          <div className="flex flex-col items-center p-3 bg-purple-100 text-purple-800 rounded-lg">
            <div className="text-2xl font-bold">{stats.uniqueCounts.locations}</div>
            <div className="text-sm">Locations</div>
          </div>
          
          {/* Sites */}
          <div className="flex flex-col items-center p-3 bg-yellow-100 text-yellow-800 rounded-lg">
            <div className="text-2xl font-bold">{stats.uniqueCounts.sites}</div>
            <div className="text-sm">Sites</div>
          </div>
          
          {/* Panels */}
          <div className="flex flex-col items-center p-3 bg-red-100 text-red-800 rounded-lg">
            <div className="text-2xl font-bold">{stats.uniqueCounts.panels}</div>
            <div className="text-sm">Panels</div>
          </div>
        </div>
      </div>

      {/* Login Statistics Card */}
      <div className="bg-white rounded-lg shadow-sm p-6 mb-6">
        <h3 className="text-xl font-semibold mb-4">Login Statistics</h3>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          {/* Total Logins */}
          <div className="flex flex-col items-center p-3 bg-green-100 text-green-800 rounded-lg">
            <div className="text-2xl font-bold">{loginStats.totalLogins}</div>
            <div className="text-sm">Total Logins</div>
          </div>
          
          {/* Today's Logins */}
          <div className="flex flex-col items-center p-3 bg-blue-100 text-blue-800 rounded-lg">
            <div className="text-2xl font-bold">{loginStats.todayLogins}</div>
            <div className="text-sm">Today's Logins</div>
          </div>
          
          {/* Unique Users */}
          <div className="flex flex-col items-center p-3 bg-indigo-100 text-indigo-800 rounded-lg">
            <div className="text-2xl font-bold">{loginStats.uniqueUsers}</div>
            <div className="text-sm">Unique Users</div>
          </div>
        </div>
      </div>

    </div>
  );
};
